/* eslint-disable prefer-const */
/* eslint-disable @typescript-eslint/no-explicit-any */
import 'dayjs/locale/lt';

import IconCheckWhite from '@assets/icons/check-white.svg?react';
import IconConfig from '@assets/icons/config.svg?react';
import IconEmpty from '@assets/icons/empty.svg?react';
import {
 Autocomplete,
 Box,
 Button,
 IconButton,
 TextField,
 Typography,
 useTheme,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
// eslint-disable-next-line unused-imports/no-unused-imports
import isoWeek from 'dayjs/plugin/isoWeek';
import { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ColPresetType } from '../../../redux/clientConfiguration/clientConfigurationSlice';
import { StructureType } from '../../../types/types';
import { findStructure, getTranslation } from '../../../utils/dataTypes';
import SelectApi from '../SelectApi';
import { CustomGridApi } from '../XTable/types';
import DateRangeFilter from './DateRangeFilter';
import OperatorMenu from './OperatorMenu';
import { DateRangeColumn } from './types';
// for start/end of week
dayjs.extend(isoWeek);

interface FilterFieldsProps {
 apiRef: MutableRefObject<CustomGridApi>;
 applyFilters: (data: FormValues) => void;
 resetFilters: () => void;
 setColModalOpen: Dispatch<SetStateAction<boolean>>;
 columns: GridColDef[];
 dateRangeColumns: DateRangeColumn[];
 currentColumnsPreset: ColPresetType | null;
 structure: StructureType[];
 isFormChanged: boolean;
}

interface FormValues {
 [key: string]: any;
}

const FilterFields = ({
 apiRef,
 applyFilters,
 resetFilters,
 setColModalOpen,
 columns,
 dateRangeColumns,
 currentColumnsPreset,
 structure,
 isFormChanged,
}: FilterFieldsProps) => {
 const { t } = useTranslation();
 const { handleSubmit, control, formState, setValue, getValues } =
  useFormContext();
 const th = useTheme();

 const noFilters = !Object.keys(
  currentColumnsPreset?.filters?.fields || {},
 ).filter((key) => findStructure(structure, key)?.Type !== 'datetime').length;

 return (
  columns && (
   <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="lt">
    <Box
     component="form"
     onSubmit={handleSubmit(applyFilters)}
     // noValidate
     sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      // gap: 2,
      // height: '100%',
      overflowY: 'auto',
      px: 3,
     }}
    >
     <Box sx={{ overflowY: 'auto', pb: 1, flex: 1 }}>
      {dateRangeColumns.length > 0 && (
       <DateRangeFilter columns={dateRangeColumns} />
      )}
      <Box
       sx={{
        // mt: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
       }}
      >
       <Typography
        sx={{
         color: th.palette.kemek.darkGray,
         fontSize: 14,
         fontWeight: 500,
         lineHeight: 'initial',
        }}
       >
        {t('global.chosenFilters')}
       </Typography>
       <IconButton onClick={() => setColModalOpen(true)} sx={{}}>
        <IconConfig
         style={{ width: 18, height: 18, color: th.palette.kemek.darkestGray }}
        />
       </IconButton>
      </Box>
      {/* {JSON.stringify(tempFiltersPreset || {})} */}
      <Box
       sx={{
        flex: 1,
        overflowY: 'auto',
        pt: '12px', // for not overflowing with label

        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        //  height: '100%',
       }}
      >
       {noFilters && (
        <Box
         sx={{
          textAlign: 'center',
          p: '12px',
          backgroundColor: th.palette.kemek.lightBlue,
          mb: 3,
         }}
        >
         <Typography variant="body2" color="text.secondary">
          {t('global.noItemsFound')}
         </Typography>
        </Box>
       )}
       {Object.keys(currentColumnsPreset?.filters?.fields || {}).map(
        (fieldKey: string) => {
         const exists = true; // columnExists(columns, fieldKey);
         const currentStructure = findStructure(structure, fieldKey);
         //  console.log('currentStructure', fieldKey, currentStructure);
         if (
          ['DateFrom', 'DateTo'].includes(fieldKey) ||
          !currentStructure ||
          currentStructure?.Type === 'datetime'
         )
          return null;

         let returnObj;
         console.log('currentStructure', currentStructure);

         switch (currentStructure?.Type) {
          case 'model':
           returnObj = (
            <SelectApi
             name={fieldKey}
             url={currentStructure.ForeignKeyUrl}
             filter={currentStructure.ForeignKeyDefaultFilter}
             label={getTranslation(structure, fieldKey)}
             isEditable
             operator="contains"
             control={control}
             setValue={setValue}
             multiple
            />
           );
           break;
          case 'array':
           returnObj = (
            <SelectApi
             name={fieldKey}
             url={currentStructure.ForeignKeyUrl}
             filter={currentStructure.ForeignKeyDefaultFilter}
             label={getTranslation(structure, fieldKey)}
             isEditable
             operator="contains"
             control={control}
             setValue={setValue}
             multiple
            />
           );
           break;
          case 'enum':
           returnObj = (
            <Controller
             name={fieldKey}
             control={control}
             defaultValue="" // Default value for controlled input
             render={({ field }) => (
              //  console.log('selectApi field', field);
              <Autocomplete
               {...field}
               multiple
               value={field.value || []}
               onChange={(_, value: any) => {
                console.log('onChange', value, _);
                setValue(fieldKey, value || '');
               }}
               options={Object.entries(currentStructure.EnumValues).map(
                ([value, label]) => ({
                 Id: value,
                 Name: label,
                }),
               )}
               renderOption={(props, option) => (
                <li
                 {...props}
                 key={`${fieldKey}-${option[fieldKey]}-${option.Id}`}
                >
                 {option.Name}
                </li>
               )}
               getOptionLabel={(option: any) =>
                (currentStructure.EnumValues[option] || option.Name).toString()
               }
               //  filterOptions={(x) => x} // Disable client-side filtering
               //  onInputChange={debounce((_, val, reason) => {
               //   // console.log('onInputChange', reason, field.value, val);
               //   if (reason === 'reset') {
               //    return;
               //   }
               //   if (reason === 'clear') {
               //    setInputValue('');
               //    //  setValue('');
               //    //  clearValue();
               //    return;
               //   }

               //   if (field.value === val) return;
               //   setInputValue(val);
               //   if (reason !== 'reset') {
               //    fetchOptions(val);
               //   }

               //   // if (field.value[filterName] === value) return;
               //   // setInputValue(value);
               //   // fetchOptions(value);
               //  }, 500)}
               isOptionEqualToValue={(option, value) =>
                (option as any).Id === value.Id
               }
               renderInput={(params) => (
                <TextField
                 {...params}
                 label={currentStructure.Translation}
                 variant="outlined"
                 style={{
                  backgroundColor: 'white',
                 }}
                />
               )}
               sx={{ width: '100%' }}
              />
             )}
            />
            // <FormControl key={fieldKey} sx={{ width: '100%' }}>
            //  <InputLabel htmlFor={fieldKey}>
            //   {currentStructure.Translation}
            //  </InputLabel>
            //  <Controller
            //   name={fieldKey}
            //   control={control}
            //   render={({ field }) => (
            //    <Select
            //     {...field}
            //     id={fieldKey}
            //     multiple
            //     renderValue={(selected: any[]) => {
            //      // selected is an array of selected values
            //      // currentStructure.EnumValues is likely an object: { value1: label1, value2: label2, ... }
            //      // We want to display the labels for the selected values, joined by ', '
            //      if (!selected || selected.length === 0) {
            //       return 'Select an option';
            //      }
            //      // If EnumValues is an object, map selected values to their labels
            //      return selected
            //       .map((val) => currentStructure.EnumValues[val] ?? val)
            //       .join(', ');
            //     }}
            //     onChange={(e: any, value: any) => {
            //      setValue(fieldKey, e.target.value || []);
            //     }}
            //     value={typeof field.value !== 'undefined' ? field.value : []}
            //     label={getTranslation(structure, fieldKey)}
            //     endAdornment={
            //      field.value && (
            //       <InputAdornment
            //        sx={{ position: 'absolute', right: 32 }}
            //        position="end"
            //       >
            //        <IconButton
            //         onClick={() => {
            //          setValue(fieldKey, '');
            //         }}
            //        >
            //         <IconClose style={{ width: 14, height: 14 }} />
            //        </IconButton>
            //       </InputAdornment>
            //      )
            //     }
            //    >
            //     {Object.entries(currentStructure.EnumValues).map(
            //      ([value, label]) => (
            //       <MenuItem key={value} value={value}>
            //        {label}
            //       </MenuItem>
            //      ),
            //     )}
            //    </Select>
            //   )}
            //  />
            // </FormControl>
           );
           break;
          default:
           returnObj = (
            <Controller
             name={fieldKey}
             control={control}
             //  defaultValue=""
             render={({ field }) => (
              <TextField
               {...field}
               value={field.value || ''}
               onChange={(e) => {
                setValue(fieldKey, e.target.value || '');
               }}
               label={getTranslation(structure, fieldKey)}
               variant="outlined"
               error={!!formState?.errors?.[fieldKey]}
               fullWidth
              />
             )}
            />
           );
           break;
         }

         return returnObj ? (
          <Box
           sx={{
            width: '100%',
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
           }}
           key={fieldKey}
          >
           <Box sx={{ flex: 1 }}>{returnObj}</Box>
           <Controller
            name={`operators[${fieldKey}]`}
            control={control}
            defaultValue=""
            //  rules={{ required: !exists ? false : 'Operator is required' }}
            render={({ field }) => (
             //  console.log('field', field.name, field.value);
             <>
              <TextField
               {...field}
               type="hidden"
               variant="standard"
               sx={{ display: 'none' }}
              />
              <OperatorMenu
               operators={[
                ...(columns.find((item) => item.field === fieldKey)
                 ?.filterOperators || []),
               ]}
               filter={fieldKey}
               columnExists={exists}
               value={field.value}
               structure={currentStructure}
              />
             </>
            )}
           />
          </Box>
         ) : null;
        },
       )}
      </Box>
     </Box>
     {!!Object.keys(currentColumnsPreset?.filters?.fields || {}).length && (
      <Box
       sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
       }}
      >
       <Button
        variant="outlined"
        color="secondary"
        endIcon={<IconEmpty style={{ color: th.palette.kemek.darkGray }} />}
        onClick={() => {
         resetFilters();
        }}
        sx={{ flex: 1 }}
       >
        {t('global.reset')}
       </Button>
       <Button
        type="submit"
        variant="contained"
        disabled={!isFormChanged}
        endIcon={
         <IconCheckWhite
          style={{ color: th.palette.kemek.white, width: 16, height: 11 }}
         />
        }
        // autoFocus
        sx={{ flex: 1, height: 44 }}
       >
        {t('global.apply')}
       </Button>
      </Box>
     )}
    </Box>
   </LocalizationProvider>
  )
 );
};
FilterFields.displayName = 'FilterFields';
export default FilterFields;
